import { Box, Button, Divider, SxProps, useMediaQuery, useTheme } from "@mui/material";
import { Typography } from "@powerdiary/components-library/lib";
import { amountToCurrency } from "../../../../../../../utils/localeHelper";
import { selectBooking } from "../../../../../bookingSlice";
import { useClientPortalConfigContext } from "../../../../../../../context/ClientPortalConfig.ctx";
import { useClientPortalSelector } from "../../../../../../../redux";
import { StripeSurcharge } from "../../../../../../../types/generated/models";

interface PaymentActionsProps {
    paymentOptional: boolean;
    paymentRequired: boolean;
    optionalPaymentEnabled: boolean;
    setOptionalPaymentEnabled: (enabled: boolean) => void;
    handlePaymentWithAmount: (amount: number) => Promise<void>;
    onBookNow: () => void;
    disabled: boolean;
    stripeSurchargeFullAmount: StripeSurcharge | null;
    discountedFullAmount: number;
    discountedDepositAmount: number;
}

export default function PaymentActions({
    paymentOptional,
    paymentRequired,
    optionalPaymentEnabled,
    setOptionalPaymentEnabled,
    handlePaymentWithAmount,
    onBookNow,
    disabled,
    stripeSurchargeFullAmount,
    discountedFullAmount,
    discountedDepositAmount,
}: PaymentActionsProps) {
    const { service } = useClientPortalSelector(selectBooking);
    const {
        clientPortalConfig: { currencySymbol },
    } = useClientPortalConfigContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

    const optionalPaymentButtonsSx: SxProps = { width: isMobile ? "100%" : "50%" };

    const optionalPaymentWrapperSx: SxProps = {
        display: "flex",
        gap: "16px",
        flexDirection: isMobile ? "column-reverse" : "row",
    };

    return (
        <>
            {service?.data?.needsDeposit && paymentOptional && (
                <Box display="flex" flexDirection="column" gap="16px" data-test-id="section_deposit_payment_actions">
                    <Button
                        onClick={() => void handlePaymentWithAmount(discountedDepositAmount)}
                        variant="contained"
                        disabled={disabled}
                        data-test-id="button_book_and_pay_deposit"
                    >
                        Book and Pay Now
                    </Button>
                    <Divider>
                        <Typography variant="paragraphMediumBold">OR</Typography>
                    </Divider>
                    <Typography textAlign="center" variant="paragraphSmallRegular">
                        You can pay the full amount now
                    </Typography>
                    <Button
                        variant="contained"
                        disabled={disabled}
                        data-test-id="button_book_and_pay_full_amount"
                        onClick={() => void handlePaymentWithAmount(discountedFullAmount)}
                    >
                        Book and Pay full {amountToCurrency(stripeSurchargeFullAmount?.totalAmount, currencySymbol)}
                    </Button>
                </Box>
            )}

            {paymentRequired ||
            (service?.data?.needsDeposit && !paymentOptional) ||
            (paymentOptional && optionalPaymentEnabled) ? (
                <Button
                    onClick={() =>
                        void handlePaymentWithAmount(
                            service?.data?.needsDeposit ? discountedDepositAmount : discountedFullAmount
                        )
                    }
                    fullWidth
                    variant="contained"
                    disabled={disabled}
                    data-test-id="button_book_and_pay_required"
                >
                    Book and Pay Now
                </Button>
            ) : (
                <></>
            )}

            {paymentOptional && !optionalPaymentEnabled && !(service?.data?.needsDeposit && paymentOptional) && (
                <Box sx={optionalPaymentWrapperSx} data-test-id="section_optional_payment_actions">
                    <Button
                        variant="outlined"
                        sx={optionalPaymentButtonsSx}
                        onClick={onBookNow}
                        disabled={disabled}
                        data-test-id="button_book_now"
                    >
                        Book now
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setOptionalPaymentEnabled(true)}
                        sx={optionalPaymentButtonsSx}
                        disabled={disabled}
                        data-test-id="button_book_and_pay_now"
                    >
                        Book and Pay Now
                    </Button>
                </Box>
            )}
        </>
    );
}
